import React, { useState, useEffect, useMemo, useRef } from 'react';
import { User, Placement } from '../types';
import { supabase_get_placements, supabase_save_placement, supabase_delete_placement, supabase_get_client_settings } from '../services/supabaseService';
import { Plus, Search, Edit3, Trash2, ChevronLeft, ChevronRight, ExternalLink, ChevronUp, ChevronDown, Users, Briefcase, RefreshCw, Building2, Check, X, Loader2 } from 'lucide-react';
import { searchTextIncludes } from '../utils/searchText';
import { formatClientFourDigitId } from '../utils/clientDisplay';
import PlacementDetail from './PlacementDetail';
import ClientDetailModal from './ClientDetailModal';
import CandidateDetailModal from './CandidateDetailModal';
import VacancyDetailModal from './VacancyDetailModal';

interface PlacementsListProps {
    user: User;
}

const STATUS_CONFIG: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    'nieuw':                { bg: 'bg-violet-50',  text: 'text-violet-700',  dot: 'bg-violet-500',  label: 'Nieuw' },
    'voorgedragen':         { bg: 'bg-violet-50',  text: 'text-violet-700',  dot: 'bg-violet-500',  label: 'Voorgedragen' },
    'goedgekeurd':          { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Goedgekeurd' },
    'afgewezen':            { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500',     label: 'Afgewezen' },
    'opnieuw voorgedragen': { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-500',  label: 'Opnieuw voorgedragen' },
    'voltooid':             { bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-500',    label: 'Voltooid' },
    'beëindigd':            { bg: 'bg-slate-50',   text: 'text-slate-700',   dot: 'bg-slate-500',   label: 'Beëindigd' },
    'actief':               { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Actief' },
    'afgerond':             { bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-500',    label: 'Afgerond' },
    'geannuleerd':          { bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500',     label: 'Geannuleerd' },
    'wacht':                { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   label: 'In Behandeling' },
    'concept':              { bg: 'bg-slate-100',  text: 'text-slate-600',   dot: 'bg-slate-400',   label: 'Concept' },
    'pending':              { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500',   label: 'Pending' },
    'approved':             { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Approved' },
    'terminated':           { bg: 'bg-slate-50',   text: 'text-slate-700',   dot: 'bg-slate-500',   label: 'Beëindigd' },
};

const getStatusStyle = (status: string) => {
    const key = (status || '').toLowerCase();
    for (const k of Object.keys(STATUS_CONFIG)) {
        if (key.includes(k)) return STATUS_CONFIG[k];
    }
    return { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400', label: status || 'Concept' };
};

type PlacementClientFilterOption = {
    /** Filterwaarde (bedrijfsnaam, zoals in de tabel). */
    value: string;
    name: string;
    number: string;
};

type SearchableFilterSelectProps = {
    value: string;
    onChange: (value: string) => void;
    options: PlacementClientFilterOption[] | string[];
    placeholder: string;
    className?: string;
};

function normalizePlacementFilterOption(opt: PlacementClientFilterOption | string): PlacementClientFilterOption {
    if (typeof opt === 'string') return { value: opt, name: opt, number: '' };
    return opt;
}

function placementFilterOptionLabel(opt: PlacementClientFilterOption): string {
    if (opt.number) return `${opt.number} ${opt.name}`;
    return opt.name;
}

function placementFilterOptionHaystack(opt: PlacementClientFilterOption): string {
    return `${opt.name} ${opt.number} ${opt.value}`.trim();
}

function buildPlacementClientFilterOptions(
    placements: Placement[],
    role: 'opdrachtgever' | 'eindklant'
): PlacementClientFilterOption[] {
    const byName = new Map<string, PlacementClientFilterOption>();
    for (const p of placements) {
        const client =
            role === 'opdrachtgever'
                ? p.opdrachtgever || p.resolved_opdrachtgever || p.vacancy?.opdrachtgever
                : p.eindklant || p.resolved_eindklant || p.vacancy?.eindklant;
        const name = String(client?.name || '').trim();
        if (!name) continue;
        const number = formatClientFourDigitId(client);
        const prev = byName.get(name);
        if (!prev || (!prev.number && number)) {
            byName.set(name, { value: name, name, number });
        }
    }
    return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name, 'nl'));
}

/** Compact searchable dropdown for lange opdrachtgever/eindklant/status-lijsten. */
const SearchableFilterSelect: React.FC<SearchableFilterSelectProps> = ({
    value,
    onChange,
    options,
    placeholder,
    className = 'w-48',
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const normalized = useMemo(
        () => options.map((o) => normalizePlacementFilterOption(o)),
        [options]
    );

    const filtered = useMemo(
        () => normalized.filter((o) => searchTextIncludes(placementFilterOptionHaystack(o), query)),
        [normalized, query]
    );

    const selected = normalized.find((o) => o.value === value);
    const active = Boolean(value);

    return (
        <div className={`relative ${className}`} ref={rootRef}>
            <button
                type="button"
                onClick={() => {
                    setOpen((v) => !v);
                    if (!open) setQuery('');
                }}
                className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg border text-[12px] font-medium transition-all ${
                    active
                        ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                        : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300'
                }`}
            >
                <span className="min-w-0 flex-1 truncate text-left">
                    {selected ? placementFilterOptionLabel(selected) : placeholder}
                </span>
                {active ? (
                    <X
                        size={12}
                        className="shrink-0 text-indigo-400 hover:text-indigo-600"
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange('');
                        }}
                    />
                ) : (
                    <ChevronDown size={12} className="shrink-0 text-slate-400" />
                )}
            </button>
            {open && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 min-w-[220px] rounded-xl border border-slate-200 bg-white shadow-xl">
                    <div className="border-b border-slate-100 p-2">
                        <div className="relative">
                            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                autoFocus
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Zoeken…"
                                className="w-full rounded-lg border border-slate-200 py-1.5 pl-7 pr-2 text-[11px] outline-none focus:border-indigo-400"
                            />
                        </div>
                    </div>
                    <div className="max-h-52 overflow-y-auto py-1">
                        <button
                            type="button"
                            onClick={() => {
                                onChange('');
                                setOpen(false);
                                setQuery('');
                            }}
                            className={`w-full px-3 py-2 text-left text-[11px] hover:bg-slate-50 ${
                                !value ? 'font-semibold text-indigo-700 bg-indigo-50/50' : 'text-slate-500'
                            }`}
                        >
                            {placeholder}
                        </button>
                        {filtered.length === 0 ? (
                            <p className="px-3 py-2 text-[11px] text-slate-400">Geen resultaten</p>
                        ) : (
                            filtered.map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                        onChange(opt.value);
                                        setOpen(false);
                                        setQuery('');
                                    }}
                                    className={`w-full px-3 py-2 text-left text-[11px] hover:bg-indigo-50 transition-colors ${
                                        value === opt.value ? 'bg-indigo-50 font-semibold text-indigo-700' : 'text-slate-700'
                                    }`}
                                >
                                    <span className="block truncate font-medium">
                                        {opt.number ? (
                                            <span className="font-mono text-slate-500 mr-1.5">{opt.number}</span>
                                        ) : null}
                                        {opt.name}
                                    </span>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const SortIcon = ({ col, sortConfig }: { col: string; sortConfig: any }) => {
    if (sortConfig?.key !== col) return <ChevronUp size={12} className="text-slate-300" />;
    return sortConfig.direction === 'asc'
        ? <ChevronUp size={12} className="text-white" />
        : <ChevronDown size={12} className="text-white" />;
};

const PlacementsList: React.FC<PlacementsListProps> = ({ user }) => {
    const isClientPortal = window.location.hostname.toLowerCase().includes('clientportal') || new URLSearchParams(window.location.search).get('clientportal') === 'true';
    const [placements, setPlacements] = useState<Placement[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPlacement, setSelectedPlacement] = useState<Placement | null>(null);
    const [selectedClientModal, setSelectedClientModal] = useState<any>(null);
    const [selectedCandidateModalId, setSelectedCandidateModalId] = useState<string | null>(null);
    const [selectedVacancyModalId, setSelectedVacancyModalId] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({ key: 'number', direction: 'desc' });
    const pageSize = 25;

    const [statusFilter, setStatusFilter] = useState('');
    const [eindklantFilter, setEindklantFilter] = useState('');
    const [opdrachtgeverFilter, setOpdrachtgeverFilter] = useState('');
    const [dateFromFilter, setDateFromFilter] = useState('');
    const [dateToFilter, setDateToFilter] = useState('');

    const [clientSettings, setClientSettings] = useState<any>(null);
    const [editingCell, setEditingCell] = useState<{ id: string, field: string } | null>(null);
    const [editValue, setEditValue] = useState('');
    const [pendingDelete, setPendingDelete] = useState<Placement | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const fetchPlacements = async (silently = false) => {
        if (!silently) setIsLoading(true);
        const effectiveClientId = (user.role === 'CLIENT' || user.role === 'CLIENT_RECRUITER') ? user.clientId : undefined;
        const portalScope = isClientPortal && (user.role === 'CLIENT' || user.role === 'CLIENT_RECRUITER');
        const [data, settings] = await Promise.all([
            supabase_get_placements(effectiveClientId, undefined, portalScope),
            supabase_get_client_settings(effectiveClientId)
        ]);
        setPlacements(data || []);
        setClientSettings(settings || {});
        setIsLoading(false);
    };

    useEffect(() => { fetchPlacements(false); }, [user.clientId, user.role, isClientPortal]);

    useEffect(() => {
        if (selectedPlacement) {
            window.dispatchEvent(new CustomEvent('wt:taskbar-add', { 
                detail: { 
                    id: selectedPlacement.id, 
                    type: 'placement', 
                    title: `Plaatsing ${selectedPlacement.number || selectedPlacement.candidate?.name || ''}`.trim() 
                } 
            }));
        }
    }, [selectedPlacement]);

    useEffect(() => {
        const handleOpenPlacement = (e: any) => {
            const pid = e.detail;
            if (pid && placements.length > 0) {
                const p = placements.find(x => x.id === pid);
                if (p) setSelectedPlacement(p);
            }
        };
        window.addEventListener('open-placement', handleOpenPlacement);
        return () => window.removeEventListener('open-placement', handleOpenPlacement);
    }, [placements]);



    const filterOptions = useMemo(() => {
        const statuses = new Set<string>();
        placements.forEach((p) => {
            if (p.status) statuses.add(p.status);
        });
        const opdrachtgevers = buildPlacementClientFilterOptions(placements, 'opdrachtgever');
        const eindklanten = buildPlacementClientFilterOptions(placements, 'eindklant').filter(
            (ek) => !opdrachtgevers.some((og) => og.value === ek.value)
        );
        return {
            statuses: Array.from(statuses).sort(),
            eindklanten,
            opdrachtgevers,
        };
    }, [placements]);

    const filteredAndSorted = useMemo(() => {
        let filtered = placements.filter(p => {
            const q = searchQuery.toLowerCase();
            const oprClient = p.opdrachtgever || p.resolved_opdrachtgever || p.vacancy?.opdrachtgever;
            const ekClient = p.eindklant || p.resolved_eindklant || p.vacancy?.eindklant;
            const oprNum = formatClientFourDigitId(oprClient);
            const ekNum = formatClientFourDigitId(ekClient);
            const matchesSearch =
                !q ||
                searchTextIncludes(p.number, q) ||
                searchTextIncludes(p.candidate?.name, q) ||
                searchTextIncludes(p.vacancy?.title, q) ||
                searchTextIncludes(p.end_client, q) ||
                searchTextIncludes(
                    `${p.data?.candidate?.firstName || ''} ${p.data?.candidate?.lastName || ''}`,
                    q
                ) ||
                searchTextIncludes(oprClient?.name, q) ||
                searchTextIncludes(ekClient?.name, q) ||
                searchTextIncludes(oprNum, q) ||
                searchTextIncludes(ekNum, q);
            const matchesStatus = !statusFilter || p.status === statusFilter;
            
            const opr = p.opdrachtgever?.name || p.resolved_opdrachtgever?.name || p.vacancy?.opdrachtgever?.name || '';
            const eindklant = p.eindklant?.name || p.resolved_eindklant?.name || p.vacancy?.eindklant?.name || '';
            
            const matchesEindklant = !eindklantFilter || eindklant === eindklantFilter;
            const matchesOpr = !opdrachtgeverFilter || opr === opdrachtgeverFilter;
            
            let matchesDateFrom = true;
            let matchesDateTo = true;
            if (dateFromFilter || dateToFilter) {
                const placementDateStr = p.start_date || p.data?.dateBegin || p.data?.jobOrder?.startDate || p.data?.dateAdded || p.created_at;
                if (placementDateStr) {
                    const placementDate = new Date(placementDateStr);
                    if (dateFromFilter) {
                        matchesDateFrom = placementDate >= new Date(dateFromFilter);
                    }
                    if (dateToFilter) {
                        const toDate = new Date(dateToFilter);
                        toDate.setHours(23, 59, 59, 999);
                        matchesDateTo = placementDate <= toDate;
                    }
                }
            }

            return matchesSearch && matchesStatus && matchesEindklant && matchesOpr && matchesDateFrom && matchesDateTo;
        });

        if (sortConfig) {
            filtered = [...filtered].sort((a, b) => {
                let av: any, bv: any;
                if (sortConfig.key === 'candidateName') {
                    av = a.candidate?.name || '';
                    bv = b.candidate?.name || '';
                } else if (sortConfig.key === 'vacancyTitle') {
                    av = a.vacancy?.title || a.data?.jobOrder?.title || '';
                    bv = b.vacancy?.title || b.data?.jobOrder?.title || '';
                } else if (sortConfig.key === 'opdrachtgever') {
                    av = a.opdrachtgever?.name || a.resolved_opdrachtgever?.name || a.vacancy?.opdrachtgever?.name || '';
                    bv = b.opdrachtgever?.name || b.resolved_opdrachtgever?.name || b.vacancy?.opdrachtgever?.name || '';
                } else if (sortConfig.key === 'recruiter') {
                    av = a.recruiter?.name || a.vacancy?.owner?.name || '';
                    bv = b.recruiter?.name || b.vacancy?.owner?.name || '';
                } else if (sortConfig.key === 'number') {
                    const avNum = parseInt((a.number || '').replace(/\D/g, ''), 10) || 0;
                    const bvNum = parseInt((b.number || '').replace(/\D/g, ''), 10) || 0;
                    return sortConfig.direction === 'asc' ? avNum - bvNum : bvNum - avNum;
                } else {
                    av = (a as any)[sortConfig.key] || '';
                    bv = (b as any)[sortConfig.key] || '';
                }
                
                if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
                if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return filtered;
    }, [placements, searchQuery, statusFilter, eindklantFilter, opdrachtgeverFilter, dateFromFilter, dateToFilter, sortConfig]);

    useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter, eindklantFilter, opdrachtgeverFilter, dateFromFilter, dateToFilter]);

    const paginatedPlacements = filteredAndSorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const totalPages = Math.ceil(filteredAndSorted.length / pageSize);

    const requestSort = (key: string) => {
        setSortConfig(prev => prev?.key === key && prev.direction === 'asc' ? { key, direction: 'desc' } : { key, direction: 'asc' });
    };

    const handleSavePlacement = async (placement: any) => {
        const result = await supabase_save_placement({ ...placement, client_id: user.clientId });
        if (result.success) { 
            setIsCreating(false); 
            // Fetch updated list immediately so relations are resolved
            const data = await supabase_get_placements(user.clientId, undefined, isClientPortal);
            setPlacements(data || []);
            const savedId = result.data?.id || placement.id;
            let updatedPlacement = data.find((p) => p.id === savedId);
            if (!updatedPlacement && result.data?.candidate_id && result.data?.vacancy_id) {
                updatedPlacement = data.find(
                    (p) =>
                        !String(p.id).startsWith('app-') &&
                        p.candidate_id === result.data.candidate_id &&
                        p.vacancy_id === result.data.vacancy_id
                );
            }
            if (updatedPlacement) setSelectedPlacement(updatedPlacement);
        }
        else alert('Error saving placement: ' + result.error);
    };

    const handleSaveInlineEdit = async (p: Placement) => {
        if (!editingCell) return;
        const result = await supabase_save_placement({ ...p, [editingCell.field]: editValue, client_id: user.clientId });
        if (result.success) {
            setPlacements((prev) =>
                prev.map((item) => {
                    if (item.id !== p.id) return item;
                    if (editingCell.field === 'contract_type') {
                        return {
                            ...item,
                            contract_type: editValue as any,
                            data: { ...(item.data || {}), contract_type: editValue },
                        };
                    }
                    return { ...item, [editingCell.field]: editValue };
                })
            );
            setEditingCell(null);
        } else {
            alert('Fout bij opslaan: ' + result.error);
        }
    };

    const renderInlineSelect = (p: Placement, field: 'placement_type' | 'employment_type' | 'contract_type', options: string[]) => {
        const isEditing = editingCell?.id === p.id && editingCell?.field === field;
        if (isEditing) {
            return (
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <select 
                        value={editValue} 
                        onChange={e => setEditValue(e.target.value)} 
                        className="text-[10px] text-slate-700 bg-white border border-blue-400 rounded px-1 py-1 outline-none w-20 shadow-sm"
                    >
                        <option value="">—</option>
                        {options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <button onClick={() => handleSaveInlineEdit(p)} className="text-emerald-600 hover:bg-emerald-50 p-1 rounded-md transition-colors"><Check size={12} strokeWidth={3} /></button>
                    <button onClick={() => setEditingCell(null)} className="text-red-500 hover:bg-red-50 p-1 rounded-md transition-colors"><X size={12} strokeWidth={3} /></button>
                </div>
            );
        }
        return (
            <div 
                onClick={(e) => { e.stopPropagation(); setEditingCell({ id: p.id, field }); setEditValue(((p as any)[field] || (p as any).data?.[field] || '') as string); }}
                className="text-[11px] text-slate-500 hover:bg-slate-100 hover:text-slate-800 px-1.5 py-0.5 -ml-1.5 rounded cursor-pointer border border-transparent hover:border-slate-200 transition-colors inline-flex max-w-[100px] truncate"
                title="Klik om aan te passen"
            >
                {(p as any)[field] || (p as any).data?.[field] || '—'}
            </div>
        );
    };

    const openDeleteConfirm = (target: Placement | string) => {
        const row =
            typeof target === 'string'
                ? placements.find((p) => p.id === target) || ({ id: target } as Placement)
                : target;
        setDeleteError(null);
        setPendingDelete(row);
    };

    const executeDeletePlacement = async (id: string) => {
        setDeleteBusy(true);
        setDeleteError(null);
        try {
            const { success, error } = await supabase_delete_placement(id);
            if (!success) {
                setDeleteError(error || 'Onbekende fout');
                return;
            }
            setPendingDelete(null);
            fetchPlacements();
            if (selectedPlacement?.id === id) setSelectedPlacement(null);
        } finally {
            setDeleteBusy(false);
        }
    };

    const confirmPendingDelete = async () => {
        if (!pendingDelete?.id) return;
        await executeDeletePlacement(pendingDelete.id);
    };

    const renderDeleteModal = () => {
        if (!pendingDelete) return null;
        const isAppRow = String(pendingDelete.id).startsWith('app-');
        const label =
            pendingDelete.candidate?.name ||
            pendingDelete.vacancy?.title ||
            pendingDelete.number ||
            pendingDelete.id;
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 animate-fadeIn">
                    <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                        <Trash2 size={24} />
                    </div>
                    <h3 className="text-lg font-black text-center text-slate-900 mb-2">Plaatsing verwijderen?</h3>
                    {isAppRow ? (
                        <p className="text-sm text-center text-slate-500 mb-3 leading-relaxed">
                            Deze rij komt uit een sollicitatie (APP). Verwijderen zet de sollicitatie op status{' '}
                            <strong>Actief</strong> en haalt hem uit de plaatsingenlijst.
                        </p>
                    ) : (
                        <p className="text-sm text-center text-slate-500 mb-3">
                            Dit is definitief en kan niet ongedaan worden gemaakt.
                        </p>
                    )}
                    <p className="text-xs font-bold text-slate-800 text-center truncate px-2 mb-4" title={label}>
                        {label}
                    </p>
                    {deleteError ? (
                        <p className="text-[12px] text-red-600 text-center mb-4 font-medium">{deleteError}</p>
                    ) : null}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                setPendingDelete(null);
                                setDeleteError(null);
                            }}
                            disabled={deleteBusy}
                            className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50"
                        >
                            Annuleren
                        </button>
                        <button
                            type="button"
                            onClick={() => void confirmPendingDelete()}
                            disabled={deleteBusy}
                            className="flex-1 py-3 bg-[#e51b23] hover:bg-red-700 text-white rounded-xl text-xs font-black uppercase tracking-widest transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {deleteBusy ? <Loader2 size={16} className="animate-spin" /> : null}
                            Verwijderen
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    if (selectedPlacement || isCreating) {
        const currentIndex = selectedPlacement ? filteredAndSorted.findIndex(p => p.id === selectedPlacement.id) : -1;
        
        return (
            <>
            {renderDeleteModal()}
            <PlacementDetail
                placement={selectedPlacement}
                user={user}
                clientSettings={clientSettings}
                onClose={() => { setSelectedPlacement(null); setIsCreating(false); }}
                onSave={handleSavePlacement}
                onDelete={executeDeletePlacement}
                onNext={() => {
                    if (currentIndex !== -1 && currentIndex < filteredAndSorted.length - 1) {
                        setSelectedPlacement(filteredAndSorted[currentIndex + 1]);
                    }
                }}
                onPrev={() => {
                    if (currentIndex > 0) {
                        setSelectedPlacement(filteredAndSorted[currentIndex - 1]);
                    }
                }}
                hasNext={currentIndex !== -1 && currentIndex < filteredAndSorted.length - 1}
                hasPrev={currentIndex > 0}
            />
            </>
        );
    }

    const SortableTh = ({ col, label, className = '' }: { col: string; label: string; className?: string }) => (
        <th
            className={`px-3 py-3 text-left text-[10px] font-black uppercase tracking-widest cursor-pointer select-none transition-colors hover:bg-slate-100 ${className}`}
            onClick={() => requestSort(col)}
        >
            <div className="flex items-center gap-1.5">
                <span>{label}</span>
                {sortConfig?.key === col
                    ? (sortConfig.direction === 'asc' ? <ChevronUp size={11} className="text-slate-600" /> : <ChevronDown size={11} className="text-slate-600" />)
                    : <ChevronUp size={11} className="text-slate-300" />
                }
            </div>
        </th>
    );

    return (
        <>
        {renderDeleteModal()}
        <div className="h-full flex flex-col bg-white font-sans">
            {/* ─── Header Bar ─── */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 shrink-0">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h1 className="text-[17px] font-black text-slate-900 tracking-tight">Plaatsingen</h1>
                        <p className="text-[11px] text-slate-400 font-medium mt-0.5">{filteredAndSorted.length} plaatsing{filteredAndSorted.length !== 1 ? 'en' : ''}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => fetchPlacements()} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
                            <RefreshCw size={15} />
                        </button>
                        {!isClientPortal && (
                        <button onClick={() => setIsCreating(true)} className="flex items-center gap-2 bg-[#e51b23] hover:bg-[#c01018] text-white px-4 py-2 rounded-lg text-[12px] font-bold transition-all shadow-sm">
                            <Plus size={14} /> Nieuwe Plaatsing
                        </button>
                        )}
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                        <input
                            type="text"
                            placeholder="Zoek kandidaat, vacature, nummer…"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[12px] w-64 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all text-slate-700 placeholder:text-slate-400"
                        />
                    </div>
                    {(isClientPortal
                        // Client portal ÍS de opdrachtgever, dus alleen Werkplek (eindklant) filteren
                        // op de eindklanten die daadwerkelijk in de plaatsingen voorkomen.
                        ? [
                            { value: eindklantFilter, onChange: setEindklantFilter, options: filterOptions.eindklanten, placeholder: 'Alle werkplekken' },
                          ]
                        : [
                            { value: statusFilter, onChange: setStatusFilter, options: filterOptions.statuses, placeholder: 'Alle statussen' },
                            { value: opdrachtgeverFilter, onChange: setOpdrachtgeverFilter, options: filterOptions.opdrachtgevers, placeholder: 'Alle opdrachtgevers' },
                            { value: eindklantFilter, onChange: setEindklantFilter, options: filterOptions.eindklanten, placeholder: 'Alle eindklanten' },
                          ]
                    ).map(({ value, onChange, options, placeholder }) => (
                        <SearchableFilterSelect
                            key={placeholder}
                            value={value}
                            onChange={onChange}
                            options={options}
                            placeholder={placeholder}
                            className={
                                placeholder.includes('status')
                                    ? 'w-40'
                                    : placeholder.includes('werkplek')
                                      ? 'w-52'
                                      : 'w-52'
                            }
                        />
                    ))}
                    
                    <div className="flex items-center gap-1.5 ml-2 border-l border-slate-200 pl-2">
                        <span className="text-[10px] font-black uppercase text-slate-400">Datum:</span>
                        <input 
                            type="date" 
                            value={dateFromFilter} 
                            onChange={(e) => setDateFromFilter(e.target.value)} 
                            className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-[11px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-300 transition-all"
                            title="Vanaf datum"
                        />
                        <span className="text-[10px] text-slate-400">-</span>
                        <input 
                            type="date" 
                            value={dateToFilter} 
                            onChange={(e) => setDateToFilter(e.target.value)} 
                            className="px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-[11px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-300 transition-all"
                            title="Tot datum"
                        />
                    </div>

                    {(statusFilter || opdrachtgeverFilter || eindklantFilter || searchQuery || dateFromFilter || dateToFilter) && (
                        <button
                            onClick={() => { setStatusFilter(''); setOpdrachtgeverFilter(''); setEindklantFilter(''); setSearchQuery(''); setDateFromFilter(''); setDateToFilter(''); }}
                            className="px-2.5 py-1.5 text-[11px] font-bold text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-slate-200 ml-1"
                        >
                            × Wis filters
                        </button>
                    )}
                </div>
            </div>

            {/* ─── Table (full screen, no card wrapper) ─── */}
            <div className="flex-1 overflow-hidden flex flex-col">
                    <div className="overflow-x-auto overflow-y-auto flex-1">
                        <table className="w-full text-left border-collapse min-w-[1100px]">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-[10px] uppercase tracking-widest">
                                    <SortableTh col="number"        label="#"              className="w-24" />
                                    <SortableTh col="status"        label="Status"         className="w-32" />
                                    <SortableTh col="candidateName" label="Kandidaat"      className="min-w-[140px]" />
                                    <SortableTh col="recruiter"     label="Recruiter"      className="min-w-[120px]" />
                                    <SortableTh col="opdrachtgever" label="Opdrachtgever"  className="min-w-[140px]" />
                                    <th className="px-3 py-3 text-left text-[10px] font-black uppercase tracking-widest min-w-[140px]">Werkplek</th>
                                    <SortableTh col="vacancyTitle"  label="Vacature"       className="min-w-[150px]" />
                                    <SortableTh col="placement_type" label="Plaatsing"     className="w-24" />
                                    <SortableTh col="employment_type" label="Dienstverband" className="w-28" />
                                    <SortableTh col="contract_type" label="Contract"       className="w-24" />
                                    <SortableTh col="start_date"    label="Start"          className="w-24" />
                                    <SortableTh col="end_date"      label="Einde"          className="w-24" />
                                    {!isClientPortal && <th className="px-3 py-3 w-16" />}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {isLoading ? (
                                    Array.from({ length: 8 }).map((_, i) => (
                                        <tr key={i} className="animate-pulse">
                                            {Array.from({ length: 11 }).map((_, j) => (
                                                <td key={j} className="px-3 py-3">
                                                    <div className="h-3 bg-slate-100 rounded w-3/4" />
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : paginatedPlacements.length === 0 ? (
                                    <tr>
                                        <td colSpan={11} className="text-center py-20 text-slate-400">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                                                    <Briefcase size={20} className="text-slate-400" />
                                                </div>
                                                <div className="text-sm font-semibold text-slate-500">Geen plaatsingen gevonden</div>
                                                <div className="text-xs text-slate-400">Pas de filters aan of maak een nieuwe plaatsing</div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedPlacements.map(p => {
                                        const opdrachtgeverName = p.opdrachtgever?.name || p.resolved_opdrachtgever?.name || p.vacancy?.opdrachtgever?.name || '';
                                        const opdrachtgeverLogo = p.opdrachtgever?.logo_url || p.resolved_opdrachtgever?.logo_url || p.vacancy?.opdrachtgever?.logo_url;
                                        const eindklantName = p.eindklant?.name || p.resolved_eindklant?.name || p.vacancy?.eindklant?.name || '';
                                        const eindklantLogo = p.eindklant?.logo_url || p.resolved_eindklant?.logo_url || p.vacancy?.eindklant?.logo_url;
                                        const candidateName = p.candidate?.name || '';
                                        const vacancyTitle = p.vacancy?.title || p.data?.jobOrder?.title || '';
                                        const recruiterName = p.recruiter?.name || p.vacancy?.recruiter?.name || p.vacancy?.owner?.name || (p.data?.jobOrder?.owner ? `${p.data.jobOrder.owner.firstName || ''} ${p.data.jobOrder.owner.lastName || ''}`.trim() : '') || (p.data?.owner ? `${p.data.owner.firstName || ''} ${p.data.owner.lastName || ''}`.trim() : '');
                                        const displayNum = p.number ? p.number.replace(/^(BH-|PL-)/, '') : '—';
                                        const sc = getStatusStyle(p.status);

                                        return (
                                            <tr
                                                key={p.id}
                                                className="hover:bg-blue-50/40 transition-colors group cursor-pointer"
                                                onClick={() => setSelectedPlacement(p)}
                                            >
                                                {/* # */}
                                                <td className="px-3 py-2.5">
                                                    <span className="text-[11px] font-mono font-bold text-slate-500">{displayNum}</span>
                                                </td>

                                                {/* Status */}
                                                <td className="px-3 py-2.5">
                                                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border ${sc.bg} ${sc.text} border-transparent`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${sc.dot}`} />
                                                        {sc.label}
                                                    </span>
                                                </td>

                                                {/* Kandidaat */}
                                                <td className="px-3 py-2.5">
                                                    <div className="flex items-center gap-1.5 group/c">
                                                        <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                                                            <Users size={11} className="text-slate-400" />
                                                        </div>
                                                        <span className="text-[12px] font-semibold text-slate-800 truncate max-w-[110px]">{candidateName || '—'}</span>
                                                        {candidateName && (
                                                            <ExternalLink size={11} className="text-slate-300 hover:text-blue-500 cursor-pointer shrink-0 opacity-0 group-hover/c:opacity-100 transition-opacity"
                                                                onClick={e => { e.stopPropagation(); setSelectedCandidateModalId(p.candidate_id || p.data?.candidate?.id); }} />
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Recruiter */}
                                                <td className="px-3 py-2.5">
                                                    <span className="text-[12px] text-slate-500 truncate max-w-[110px] block">{recruiterName || '—'}</span>
                                                </td>

                                                {/* Opdrachtgever */}
                                                <td className="px-3 py-2.5">
                                                    <div className="flex items-center gap-1.5 group/o">
                                                        {opdrachtgeverLogo
                                                            ? <img src={opdrachtgeverLogo} className="w-5 h-5 rounded object-contain border border-slate-100 bg-white shrink-0" alt="" />
                                                            : <div className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center shrink-0"><Briefcase size={10} className="text-slate-400" /></div>
                                                        }
                                                        <span className="text-[12px] font-medium text-slate-700 truncate max-w-[110px]">{opdrachtgeverName || '—'}</span>
                                                        {opdrachtgeverName && (
                                                            <ExternalLink size={11} className="text-slate-300 hover:text-blue-500 cursor-pointer shrink-0 opacity-0 group-hover/o:opacity-100 transition-opacity"
                                                                onClick={e => { e.stopPropagation(); setSelectedClientModal(p.vacancy?.opdrachtgever || p.vacancy?.client || { id: p.data?.opdrachtgever_id || p.vacancy?.client_id || p.client_id, name: opdrachtgeverName }); }} />
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Eindklant */}
                                                <td className="px-3 py-2.5">
                                                    <div className="flex items-center gap-1.5 group/e">
                                                        {eindklantLogo
                                                            ? <img src={eindklantLogo} className="w-5 h-5 rounded object-contain border border-slate-100 bg-white shrink-0" alt="" />
                                                            : eindklantName ? <div className="w-5 h-5 rounded bg-slate-100 flex items-center justify-center shrink-0"><Briefcase size={10} className="text-slate-400" /></div> : null
                                                        }
                                                        <span className="text-[12px] text-slate-600 truncate max-w-[110px]">{eindklantName || '—'}</span>
                                                        {eindklantName && (
                                                            <ExternalLink size={11} className="text-slate-300 hover:text-blue-500 cursor-pointer shrink-0 opacity-0 group-hover/e:opacity-100 transition-opacity"
                                                                onClick={e => { e.stopPropagation(); setSelectedClientModal(p.vacancy?.eindklant || { id: p.data?.eindklant_id || p.vacancy?.eindklant_id, name: eindklantName }); }} />
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Vacature */}
                                                <td className="px-3 py-2.5">
                                                    <div className="flex items-center gap-1.5 group/v">
                                                        <span className="text-[12px] font-medium text-slate-700 truncate max-w-[130px]">{vacancyTitle || '—'}</span>
                                                        {vacancyTitle && (
                                                            <ExternalLink size={11} className="text-slate-300 hover:text-blue-500 cursor-pointer shrink-0 opacity-0 group-hover/v:opacity-100 transition-opacity"
                                                                onClick={e => { e.stopPropagation(); setSelectedVacancyModalId(p.vacancy_id || p.data?.jobOrder?.id); }} />
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Types */}
                                                <td className="px-3 py-2.5">
                                                    {renderInlineSelect(p, 'placement_type', clientSettings?.placement_types || ['Deta-vast', 'Detachering', 'Freelance', 'Werving & Selectie'])}
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    {renderInlineSelect(p, 'employment_type', clientSettings?.employment_types || ['Voltijd', 'Deeltijd', 'Oproep', 'Stage'])}
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    {renderInlineSelect(p, 'contract_type', clientSettings?.contract_types || ['Bepaalde Tijd', 'Onbepaalde Tijd', 'Projectbasis'])}
                                                </td>

                                                {/* Start */}
                                                <td className="px-3 py-2.5">
                                                    <span className="text-[11px] font-medium text-slate-600">
                                                        {p.start_date ? new Date(p.start_date).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                                                    </span>
                                                </td>

                                                {/* Einde */}
                                                <td className="px-3 py-2.5">
                                                    <span className="text-[11px] font-medium text-slate-600">
                                                        {p.end_date ? new Date(p.end_date).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                                                    </span>
                                                </td>

                                                {/* Actions */}
                                                {!isClientPortal && (
                                                <td className="px-3 py-2.5 text-right">
                                                    <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button onClick={e => { e.stopPropagation(); setSelectedPlacement(p); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                                                            <Edit3 size={13} />
                                                        </button>
                                                        <button onClick={e => { e.stopPropagation(); openDeleteConfirm(p); }} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" title="Verwijderen">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    </div>
                                                </td>
                                                )}
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="border-t border-slate-100 px-6 py-2.5 flex items-center justify-between bg-white shrink-0">
                        <span className="text-[11px] text-slate-400 font-medium">
                            {filteredAndSorted.length === 0 ? 'Geen resultaten' : `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, filteredAndSorted.length)} van ${filteredAndSorted.length}`}
                        </span>
                        <div className="flex items-center gap-1.5">
                            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                                className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-30 transition-colors">
                                <ChevronLeft size={14} />
                            </button>
                            <span className="text-[11px] font-bold text-slate-600 px-2 min-w-[60px] text-center">{currentPage} / {Math.max(1, totalPages)}</span>
                            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
                                className="p-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-30 transition-colors">
                                <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>
            </div>

            {selectedClientModal && (
                <ClientDetailModal 
                    client={selectedClientModal}
                    currentUserRole={user.role}
                    currentUserClientId={user.clientId}
                    accessProfiles={[]}
                    onClose={() => setSelectedClientModal(null)}
                    onSaveSuccess={() => { setSelectedClientModal(null); fetchPlacements(true); }}
                />
            )}

            {selectedCandidateModalId && (
                <CandidateDetailModal 
                    candidateId={selectedCandidateModalId}
                    clientId={user.clientId}
                    currentUser={user}
                    onClose={() => setSelectedCandidateModalId(null)}
                    onUpdate={() => fetchPlacements(true)}
                />
            )}

            {selectedVacancyModalId && (
                <VacancyDetailModal 
                    vacancyId={selectedVacancyModalId}
                    currentUser={user}
                    onClose={() => setSelectedVacancyModalId(null)}
                />
            )}
        </div>
        </>
    );
};

export default PlacementsList;
